import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Button, Divider, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { getBetupMatchIdByVirtualSyncId, getVirtualSeason, getVirtualSeasonMatches, getVirtualSeasonTable } from '../../Api';
import { VirtualProviderMatchSummaryDto, VirtualProviderSeasonDto, VirtualProviderTournamentStandingDto } from '../../models/dtos/VirtualProviderCatalogDto';
import { formatKickoff, MatchTeams } from './VirtualMatchesTable';

function matchStateColor(state: VirtualProviderMatchSummaryDto['state']): 'default' | 'success' | 'info' | 'warning' {
    switch (state) {
        case 'Live': return 'success';
        case 'Finished': return 'info';
        case 'Cancelled': return 'warning';
        default: return 'default';
    }
}

export default function VirtualSeasonDetailsDialog(props: {
    seasonId: string | null;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const [season, setSeason] = React.useState<VirtualProviderSeasonDto | null>(null);
    const [standings, setStandings] = React.useState<VirtualProviderTournamentStandingDto[]>([]);
    const [matches, setMatches] = React.useState<VirtualProviderMatchSummaryDto[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');
    const [resolvingMatchId, setResolvingMatchId] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!props.seasonId) return;
        let cancelled = false;
        setSeason(null);
        setStandings([]);
        setMatches([]);
        setError('');
        setNotice('');
        setLoading(true);
        Promise.all([
            getVirtualSeason(props.seasonId),
            getVirtualSeasonTable(props.seasonId),
            getVirtualSeasonMatches(props.seasonId),
        ])
            .then(([seasonResult, tableResult, matchesResult]) => {
                if (cancelled) return;
                setSeason(seasonResult);
                setStandings(tableResult);
                setMatches(matchesResult);
            })
            .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load season details'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [props.seasonId]);

    const openMatch = async (match: VirtualProviderMatchSummaryDto) => {
        setResolvingMatchId(match.id);
        setNotice('');
        try {
            const betupMatchId = await getBetupMatchIdByVirtualSyncId(match.id);
            if (betupMatchId == null) {
                setNotice('This match has not been synchronized to the main catalog yet.');
                return;
            }
            navigate(`/matches/${betupMatchId}`);
        } finally {
            setResolvingMatchId(null);
        }
    };

    return (
        <Dialog open={props.seasonId != null} onClose={props.onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                {season ? `Season ${season.seasonNumber} · ${season.leagueType}` : 'Season details'}
            </DialogTitle>
            <DialogContent dividers>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                {notice ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert> : null}
                {loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box> : season ? (
                    <Stack spacing={3}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip label={season.status} color={season.status === 'ACTIVE' ? 'success' : 'default'} />
                            <Chip variant="outlined" label={`Round ${season.currentRound}/${season.totalRounds}`} />
                            <Chip variant="outlined" label={season.speed} />
                            <Chip variant="outlined" label={`Starts ${new Date(season.startsAt).toLocaleString()}`} />
                            {season.endedAt ? <Chip variant="outlined" label={`Ended ${new Date(season.endedAt).toLocaleString()}`} /> : null}
                        </Stack>

                        {standings.length > 0 ? (
                            <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Tournament table</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>#</TableCell>
                                                <TableCell>Team</TableCell>
                                                <TableCell align="right">P</TableCell>
                                                <TableCell align="right">W</TableCell>
                                                <TableCell align="right">D</TableCell>
                                                <TableCell align="right">L</TableCell>
                                                <TableCell align="right">SF</TableCell>
                                                <TableCell align="right">SA</TableCell>
                                                <TableCell align="right">Diff</TableCell>
                                                <TableCell align="right">Pts</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {standings.map(standing => (
                                                <TableRow key={standing.team.id}>
                                                    <TableCell>{standing.rank}</TableCell>
                                                    <TableCell>{standing.team.name}</TableCell>
                                                    <TableCell align="right">{standing.played}</TableCell>
                                                    <TableCell align="right">{standing.won}</TableCell>
                                                    <TableCell align="right">{standing.drawn}</TableCell>
                                                    <TableCell align="right">{standing.lost}</TableCell>
                                                    <TableCell align="right">{standing.scoreFor}</TableCell>
                                                    <TableCell align="right">{standing.scoreAgainst}</TableCell>
                                                    <TableCell align="right">{standing.scoreDifference}</TableCell>
                                                    <TableCell align="right"><strong>{standing.points}</strong></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ) : null}

                        <Divider />

                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Matches</Typography>
                            {matches.length === 0 ? (
                                <Typography color="text.secondary">No matches scheduled yet.</Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Match</TableCell>
                                                <TableCell>Kickoff</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="right">Score</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {matches.map(match => (
                                                <TableRow key={match.id} hover sx={{ cursor: 'pointer' }} onClick={() => openMatch(match)}>
                                                    <TableCell><MatchTeams match={match} size={20} /></TableCell>
                                                    <TableCell>{formatKickoff(match.startDate)}</TableCell>
                                                    <TableCell><Chip size="small" color={matchStateColor(match.state)} label={match.state} /></TableCell>
                                                    <TableCell align="right">{match.state === 'Future' ? '—' : `${match.scoreHome} : ${match.scoreAway}`}</TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            size="small"
                                                            disabled={resolvingMatchId === match.id}
                                                            onClick={event => { event.stopPropagation(); openMatch(match); }}
                                                        >
                                                            {resolvingMatchId === match.id ? <CircularProgress size={16} /> : 'Open'}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
